import React, { useEffect, useState } from "react";
import { formatSomFromApi, formatSomNumber } from "../utils/apiMoney";
import { getClientMe, isBasicConfigured, postClient } from "../api/protected";
import { posterGetSettings } from "../api/poster";
import { asPosterInner } from "../poster/response";
import {
  getAccountUrl,
  getPosterToken,
  setAccountUrl,
  setCachedPosterUser,
  setPosterToken,
} from "../session";
import { toast } from "react-toastify";
import { MOCK_USER } from "../mocks";
import { PageLoader } from "./Loader";

const IS_DEV = process.env.REACT_APP_DEV === "true";

const Field = ({ label, value }) => {
  if (value == null || value === "") return null;
  return (
    <div className="posterField">
      <div className="posterField__label">{label}</div>
      <div className="posterField__value">{String(value)}</div>
    </div>
  );
};

/**
 * @param {unknown} tz
 */
function formatTimezone(tz) {
  if (tz == null) return "—";
  if (typeof tz === "string") return tz;
  if (typeof tz === "object" && "timezone_name" in /** @type {any} */ (tz)) {
    return String(/** @type {any} */ (tz).timezone_name);
  }
  if (typeof tz === "object" && "name" in /** @type {any} */ (tz)) {
    return String(/** @type {any} */ (tz).name);
  }
  try {
    return JSON.stringify(tz);
  } catch {
    return "—";
  }
}

function formatCurrencyText(cur) {
  if (!cur || typeof cur !== "object") return null;
  const c = /** @type {any} */ (cur);
  return [
    c.currency_name,
    c.currency_symbol,
    c.currency_code_iso && `(ISO: ${c.currency_code_iso})`,
  ]
    .filter(Boolean)
    .join(" · ");
}

/**
 * @param {unknown} d
 * @returns {string}
 */
function formatClientDate(d) {
  if (d == null) {
    return "—";
  }
  const t = new Date(/** @type {string|number|Date} */(d));
  if (isNaN(t.getTime())) {
    return "—";
  }
  return t.toLocaleDateString("ru-RU", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

/**
 * URL аккаунта для <code>POST /clients</code> (тот же в query и в body).
 * @param {Record<string, any>} settings
 */
function resolveAccountUrlForClient(settings) {
  const a = settings?.account_url || settings?.COMPANY_ID;
  if (typeof a === "string" && a.trim()) {
    return a.trim();
  }
  return (getAccountUrl() || "").trim();
}

/**
 * @param {Record<string, any>} settings
 */
async function ensureClientFromSettings(settings) {
  if (!settings || !isBasicConfigured()) {
    return;
  }
  const accountUrl = resolveAccountUrlForClient(settings);
  if (!accountUrl) {
    return;
  }
  const name =
    String(settings.company_name || settings.name || "Клиент").trim() ||
    "Клиент";
  const phoneRaw = settings.pos_phone || settings.FIZ_ADRESS_PHONE;
  const body = {
    account_url: accountUrl,
    name,
    balance: 0,
  };
  if (phoneRaw != null && String(phoneRaw).trim()) {
    body.phone = String(phoneRaw).trim();
  }
  try {
    await postClient(accountUrl, body);
    setAccountUrl(accountUrl);
  } catch (e) {
    if (/** @type {any} */ (e).response?.status === 409) {
      setAccountUrl(accountUrl);
    } else {
      console.warn("POST /clients после настроек:", e);
    }
  }
}

/**
 * @param {{ me: Record<string, any> | null }} props
 */
function EposClientAccountBlock({ me }) {
  if (!me) {
    return null;
  }
  const hasBalance = me.balance != null && me.balance !== "";
  const balFormatted = hasBalance ? formatSomFromApi(me.balance) : null;

  return (
    <div className="eposClientBlock">
      <h3 className="settingsPage__subtitle settingsPage__infoColTitle">
        Аккаунт E-pos
      </h3>
      <div className="posterProfileCard accountCard eposClientBlock__card">
        {(me.activeTabletsCount != null || hasBalance) && (
          <div className="eposClientStats">
            {hasBalance && balFormatted != null && (
              <div
                className="eposClientStat eposClientStat--balance"
                aria-label={`Баланс: ${balFormatted} сум`}
              >
                <div className="eposClientStat__value eposClientStat__value--balance">
                  {balFormatted}
                </div>
                <div className="eposClientStat__currency">сум</div>
                <div className="eposClientStat__label">Баланс</div>
              </div>
            )}
            {me.activeTabletsCount != null && (
              <div
                className="eposClientStat"
                aria-label={`Активных касс: ${me.activeTabletsCount}`}
              >
                <div className="eposClientStat__value">
                  {String(me.activeTabletsCount)}
                </div>
                <div className="eposClientStat__label">Активных касс</div>
              </div>
            )}
            {me.activeTabletsCount != null && (
              <div
                className="eposClientStat eposClientStat--balance"
                aria-label={`Ежемесячный платёж: ${formatSomNumber(Number(me.activeTabletsCount) * 100000)} сум`}
              >
                <div className="eposClientStat__value eposClientStat__value--balance">
                  {formatSomNumber(Number(me.activeTabletsCount) * 100000)}
                </div>
                <div className="eposClientStat__currency">сум</div>
                <div className="eposClientStat__label">Ежемесячный платёж</div>
              </div>
            )}
          </div>
        )}
        <div className="posterProfileCard__fields">
          {me.name != null && me.name !== "" && (
            <Field label="Название" value={String(me.name)} />
          )}
          {me.account_url != null && me.account_url !== "" && (
            <Field label="URL аккаунта" value={String(me.account_url)} />
          )}
          {me.phone != null && me.phone !== "" && (
            <Field label="Телефон" value={String(me.phone)} />
          )}
          {me.createdAt != null && me.createdAt !== "" && (
            <Field
              label="Регистрация"
              value={formatClientDate(me.createdAt)}
            />
          )}
        </div>
      </div>
    </div>
  );
}

export default function Settings() {
  const token = getPosterToken();
  const [s, setS] = useState(/** @type {Record<string, any> | null} */ (null));
  const [load, setLoad] = useState(true);
  const [err, setErr] = useState(/** @type {string | null} */ (null));
  const [clientMe, setClientMe] = useState(
    /** @type {Record<string, any> | null} */ (null),
  );

  useEffect(() => {
    if (IS_DEV && !getPosterToken() && MOCK_USER?.access_token) {
      setPosterToken(String(MOCK_USER.access_token));
      setCachedPosterUser(MOCK_USER);
    }
    const effectiveToken = getPosterToken();
    if (!effectiveToken) {
      setLoad(false);
      return;
    }
    let c = false;
    (async () => {
      try {
        const raw = await posterGetSettings(effectiveToken);
        const inner = asPosterInner(raw);
        const next = /** @type {any} */ (inner) || null;
        if (!c) {
          setS(next);
        }
        if (!c && next) {
          await ensureClientFromSettings(next);
        }
      } catch (e) {
        if (!c) {
          setErr(/** @type {any} */ (e).message);
          toast.error("Настройки Poster недоступны");
        }
      } finally {
        if (!c) {
          setLoad(false);
        }
      }
    })();
    return () => {
      c = true;
    };
  }, []);

  useEffect(() => {
    if (load) {
      return;
    }
    if (!isBasicConfigured() || !getPosterToken()) {
      setClientMe(null);
      return;
    }
    const acc = (getAccountUrl() || "").trim();
    if (!acc) {
      setClientMe(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const raw = await getClientMe(acc);
        if (!cancelled) {
          setClientMe(
            /** @type {any} */ (raw) && typeof raw === "object"
              ? /** @type {Record<string, any>} */(raw)
              : null,
          );
        }
      } catch (e) {
        console.warn("GET /clients/me", e);
        if (!cancelled) {
          setClientMe(null);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [load, s, err, token]);

  if (load) {
    return (
      <div className="settingsPage">
        <h2 className="settingsPage__title">Настройки</h2>
        <PageLoader label="Настройки" />
      </div>
    );
  }

  if (!token) {
    return (
      <div className="settingsPage">
        <h2 className="settingsPage__title">Настройки</h2>
        <p className="text-muted">
          Нет токена Poster. Откройте приложение по ссылке OAuth.
        </p>
      </div>
    );
  }

  if (err && !s) {
    return (
      <div className="settingsPage">
        <h2 className="settingsPage__title">Настройки</h2>
        <div className="settingsPage__infoRow">
          <div className="settingsPage__infoCol">
            <p className="text-danger m-0">{err}</p>
          </div>
          <div className="settingsPage__infoCol settingsPage__infoCol--ePos">
            <EposClientAccountBlock me={clientMe} />
          </div>
        </div>
      </div>
    );
  }

  const c = s?.currency;
  const curText = formatCurrencyText(c);

  return (
    <div className="settingsPage">
      <h2 className="settingsPage__title">Профиль заведения (Poster)</h2>

      <div className="settingsPage__infoRow">
        <div className="settingsPage__infoCol">
          <h3 className="settingsPage__subtitle settingsPage__infoColTitle">
            Профиль Poster
          </h3>
          <div className="posterProfileCard accountCard">
            <div className="accountCard__header accountCard__header--text">
              <div>
                <div className="accountCard__label">Название</div>
                <div className="accountCard__client posterProfileCard__titleText">
                  {s?.company_name || "—"}
                </div>
              </div>
            </div>

            {curText && (
              <div className="posterProfileCard__currencyText mb-2">
                <div className="accountCard__label">Валюта</div>
                <div
                  className="accountCard__client"
                  style={{ fontSize: "1rem" }}
                >
                  {curText}
                </div>
              </div>
            )}

            <div className="posterProfileCard__fields">
              <Field
                label="Часовой пояс"
                value={
                  s?.timezones != null ? formatTimezone(s.timezones) : "—"
                }
              />
              <Field label="Язык интерфейса (Poster)" value={s?.lang} />
              {s?.email && <Field label="E-mail" value={s.email} />}
              <Field label="Имя / контакт" value={s?.name} />
              {(s?.pos_phone || s?.FIZ_ADRESS_PHONE) && (
                <div className="posterField">
                  <div className="posterField__label">Контакты</div>
                  <div className="d-flex flex-column gap-1 small">
                    {s?.pos_phone && <span>POS: {String(s.pos_phone)}</span>}
                    {s?.FIZ_ADRESS_PHONE && (
                      <span>Адр. (тел.): {String(s.FIZ_ADRESS_PHONE)}</span>
                    )}
                  </div>
                </div>
              )}
              <Field
                label="Адрес (FIZ / город и др.)"
                value={s?.FIZ_ADRESS_CITY}
              />
            </div>
          </div>
        </div>

        <div className="settingsPage__infoCol settingsPage__infoCol--ePos">
          <EposClientAccountBlock me={clientMe} />
        </div>
      </div>
    </div>
  );
}
